import React, { useCallback, useRef, useEffect, useState, forwardRef } from 'react';

const CustomSlider = forwardRef(({
    id,
    label,
    min = 0,
    max = 100,
    step = 1,
    minValue,
    maxValue,
    value,
    onChange,
    onInput,
    valueLabelFormat = (val) => val,
    className = "",
    disabled = false,
    canMinMaxValueSame = false,
    style,
    preventWheel = false,
    minCaption,
    maxCaption,
    barLeftColor,
    barRightColor,
    barInnerColor,
    thumbLeftColor,
    thumbRightColor
}, ref) => {
    // Handle backward compatibility with value prop
    let initialMinValue = minValue !== undefined ? minValue : (value ? value[0] : min + (max - min) * 0.25);
    let initialMaxValue = maxValue !== undefined ? maxValue : (value ? value[1] : min + (max - min) * 0.75);

    // Validate and constrain initial values
    if (initialMinValue < min) initialMinValue = min;
    if (initialMinValue > max) initialMinValue = max;
    if (initialMaxValue < initialMinValue) initialMaxValue = initialMinValue + (canMinMaxValueSame ? 0 : step);
    if (initialMaxValue > max) initialMaxValue = max;
    if (initialMaxValue < min) initialMaxValue = min;

    const [minVal, setMinVal] = useState(Number(initialMinValue));
    const [maxVal, setMaxVal] = useState(Number(initialMaxValue));
    const [isChanging, setIsChanging] = useState(true);
    const [barMin, setBarMin] = useState(0);
    const [barMax, setBarMax] = useState(0);

    const minValRef = useRef(null);
    const maxValRef = useRef(null);
    const containerRef = useRef(null);

    const wheelTimeout = useRef(null);
    const triggerTimeout = useRef(null);
    const stepValue = canMinMaxValueSame ? 0 : step;
    const fixed = step.toString().includes('.') ? 2 : 0;



    // Input change handlers
    const handleMinChange = useCallback((e) => {
        if (disabled) return;
        let newMin = parseFloat(e.target.value);
        if (newMin > maxVal - stepValue) {
            newMin = maxVal - stepValue;
        }
        setMinVal(newMin);
        setIsChanging(true);
    }, [maxVal, stepValue, disabled]);

    const handleMaxChange = useCallback((e) => {
        if (disabled) return;
        let newMax = parseFloat(e.target.value);
        if (newMax < minVal + stepValue) {
            newMax = minVal + stepValue;
        }
        setMaxVal(newMax);
        setIsChanging(true);
    }, [minVal, stepValue, disabled]);

    // Mouse event handlers for thumbs
    const onLeftThumbMousedown = useCallback((e) => {
        if (disabled) return;
        e.preventDefault();

        const startX = e.clientX;
        const container = containerRef.current;
        const barBox = container.getBoundingClientRect();
        const barValue = minVal;
        setIsChanging(false);

        const onMouseMove = (e) => {
            const clientX = e.clientX;
            const dx = clientX - startX;
            const per = dx / barBox.width;
            let val = barValue + (max - min) * per;

            val = parseFloat(val.toFixed(fixed));
            // snap to step
            if (step > 0) {
                val = Math.round((val - min) / step) * step + min;
                val = parseFloat(val.toFixed(fixed));
            }
            if (val < min) {
                val = min;
            } else if (val > maxVal - stepValue) {
                val = maxVal - stepValue;
            }
            setMinVal(val);
        };

        const onMouseUp = () => {
            setIsChanging(true);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [disabled, minVal, maxVal, stepValue, min, max, fixed, step]);

    const onRightThumbMousedown = useCallback((e) => {
        if (disabled) return;
        e.preventDefault();

        const startX = e.clientX;
        const container = containerRef.current;
        const barBox = container.getBoundingClientRect();
        const barValue = maxVal;
        setIsChanging(false);

        const onMouseMove = (e) => {
            const clientX = e.clientX;
            const dx = clientX - startX;
            const per = dx / barBox.width;
            let val = barValue + (max - min) * per;

            val = parseFloat(val.toFixed(fixed));
            // snap to step
            if (step > 0) {
                val = Math.round((val - min) / step) * step + min;
                val = parseFloat(val.toFixed(fixed));
            }
            if (val < minVal + stepValue) {
                val = minVal + stepValue;
            } else if (val > max) {
                val = max;
            }
            setMaxVal(val);
        };

        const onMouseUp = () => {
            setIsChanging(true);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [disabled, minVal, maxVal, stepValue, min, max, fixed, step]);

    // Touch event handlers
    const onLeftThumbTouchStart = useCallback((e) => {
        if (disabled) return;

        const startX = e.touches[0].clientX;
        const container = containerRef.current;
        const barBox = container.getBoundingClientRect();
        const barValue = minVal;
        setIsChanging(false);

        const onTouchMove = (e) => {
            const clientX = e.touches[0].clientX;
            const dx = clientX - startX;
            const per = dx / barBox.width;
            let val = barValue + (max - min) * per;

            val = parseFloat(val.toFixed(fixed));
            // snap to step
            if (step > 0) {
                val = Math.round((val - min) / step) * step + min;
                val = parseFloat(val.toFixed(fixed));
            }
            if (val < min) {
                val = min;
            } else if (val > maxVal - stepValue) {
                val = maxVal - stepValue;
            }
            setMinVal(val);
        };

        const onTouchEnd = () => {
            setIsChanging(true);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };

        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('touchend', onTouchEnd);
    }, [disabled, minVal, maxVal, stepValue, min, max, fixed, step]);

    const onRightThumbTouchStart = useCallback((e) => {
        if (disabled) return;

        const startX = e.touches[0].clientX;
        const container = containerRef.current;
        const barBox = container.getBoundingClientRect();
        const barValue = maxVal;
        setIsChanging(false);

        const onTouchMove = (e) => {
            const clientX = e.touches[0].clientX;
            const dx = clientX - startX;
            const per = dx / barBox.width;
            let val = barValue + (max - min) * per;

            val = parseFloat(val.toFixed(fixed));
            // snap to step
            if (step > 0) {
                val = Math.round((val - min) / step) * step + min;
                val = parseFloat(val.toFixed(fixed));
            }
            if (val < minVal + stepValue) {
                val = minVal + stepValue;
            } else if (val > max) {
                val = max;
            }
            setMaxVal(val);
        };

        const onTouchEnd = () => {
            setIsChanging(true);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };

        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('touchend', onTouchEnd);
    }, [disabled, minVal, maxVal, stepValue, min, max, fixed, step]);

    // Mouse wheel handler
    const onMouseWheel = useCallback((e) => {
        if (disabled || preventWheel) return;
        if (!e.shiftKey && !e.ctrlKey) return;

        e.preventDefault();

        let val = (max - min) / 100;
        if (val > 1) val = 1;
        if (e.deltaY < 0) val = -val;

        let newMinVal = minVal;
        let newMaxVal = maxVal;

        if (e.shiftKey && e.ctrlKey) {
            // Move both thumbs together
            if (newMinVal + val >= min && newMaxVal + val <= max) {
                newMinVal = newMinVal + val;
                newMaxVal = newMaxVal + val;
            }
        } else if (e.ctrlKey) {
            // Move max thumb
            val = newMaxVal + val;
            if (val < newMinVal + stepValue) {
                val = newMinVal + stepValue;
            } else if (val > max) {
                val = max;
            }
            newMaxVal = val;
        } else if (e.shiftKey) {
            // Move min thumb
            val = newMinVal + val;
            if (val < min) {
                val = min;
            } else if (val > newMaxVal - stepValue) {
                val = newMaxVal - stepValue;
            }
            newMinVal = val;
        }

        setIsChanging(false);
        setMinVal(newMinVal);
        setMaxVal(newMaxVal);

        if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
        wheelTimeout.current = setTimeout(() => {
            setIsChanging(true);
        }, 100);
    }, [disabled, preventWheel, minVal, maxVal, stepValue, min, max]);

    // Bar click handlers
    const onBarLeftClick = useCallback((e) => {
        if (disabled) return;
        let newMinVal = minVal - step;
        if (newMinVal < min) {
            newMinVal = min;
        }
        setMinVal(newMinVal);
    }, [disabled, minVal, step, min]);

    const onBarRightClick = useCallback((e) => {
        if (disabled) return;
        let newMaxVal = maxVal + step;
        if (newMaxVal > max) {
            newMaxVal = max;
        }
        setMaxVal(newMaxVal);
    }, [disabled, maxVal, step, max]);

    const onInnerBarLeftClick = useCallback((e) => {
        if (disabled) return;
        let newMinVal = minVal + step;
        if (newMinVal > maxVal - stepValue) {
            newMinVal = maxVal - stepValue;
        }
        setMinVal(newMinVal);
    }, [disabled, minVal, maxVal, stepValue, step]);

    const onInnerBarRightClick = useCallback((e) => {
        if (disabled) return;
        let newMaxVal = maxVal - step;
        if (newMaxVal < minVal + stepValue) {
            newMaxVal = minVal + stepValue;
        }
        setMaxVal(newMaxVal);
    }, [disabled, maxVal, minVal, stepValue, step]);

    // Effect to handle prop changes
    useEffect(() => {
        if (minValue !== undefined) {
            let _minValue = Number(minValue);
            if (_minValue < min) _minValue = min;
            if (_minValue > max) _minValue = max;
            setIsChanging(false);
            setMinVal(_minValue);
        }
    }, [minValue, min, max]);

    useEffect(() => {
        if (maxValue !== undefined) {
            let _maxValue = Number(maxValue);
            if (_maxValue > max) _maxValue = max;
            if (_maxValue < min) _maxValue = min;
            setIsChanging(false);
            setMaxVal(_maxValue);
        }
    }, [maxValue, min, max]);

    // Effect to handle value prop changes (backward compatibility)
    useEffect(() => {
        if (value && Array.isArray(value)) {
            setIsChanging(false);
            setMinVal(Number(value[0]));
            setMaxVal(Number(value[1]));
        }
    }, [value]);

    // Effect to trigger callbacks
    useEffect(() => {
        if (maxVal < minVal) {
            console.error('maxValue is less than minValue');
            return;
        }

        const triggerChange = () => {
            const result = {
                min,
                max,
                minValue: minVal,
                maxValue: maxVal
            };

            if (isChanging && onChange) {
                onChange(value ? [minVal, maxVal] : result);
            }
            if (onInput) {
                onInput(value ? [minVal, maxVal] : result);
            }
        };

        let _barMin = ((minVal - min) / (max - min)) * 100;
        setBarMin(_barMin);
        let _barMax = ((max - maxVal) / (max - min)) * 100;
        setBarMax(_barMax);

        if (triggerTimeout.current) clearTimeout(triggerTimeout.current);
        triggerTimeout.current = setTimeout(triggerChange, 20);
    }, [minVal, maxVal, min, max, fixed, onChange, onInput, isChanging, value]);

    // Effect for wheel event prevention
    useEffect(() => {
        if (containerRef.current) {
            const container = containerRef.current;
            const handleWheel = (e) => {
                if (!e.shiftKey && !e.ctrlKey) return;
                e.preventDefault();
            };

            container.addEventListener('wheel', handleWheel);
            return () => container.removeEventListener('wheel', handleWheel);
        }
    }, []);

    const displayMinCaption = minCaption || valueLabelFormat(minVal);
    const displayMaxCaption = maxCaption || valueLabelFormat(maxVal);

    return (
        <div
            ref={ref || containerRef}
            id={id}
            className={`custom-slider w-full px-4 ${className} ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
            style={style}
            onWheel={onMouseWheel}
        >
            {/* Label */}
            {label && (
            <h3 className="text-sm font-medium mb-4" style={{ color: '#3b372e' }}>
                {label}
            </h3>
            )}

                        {/* Slider Container */}
            <div className="relative mb-4" ref={containerRef} style={{ height: '32px' }}>
                {/* Track Background with clickable areas */}
                <div className="slider-track rounded-full absolute left-0 right-0" style={{ height: '3px', top: '50%', transform: 'translateY(-50%)' }}>
                    {/* Left clickable area */}
                    <div
                        className="absolute left-0 rounded-l-full cursor-pointer"
                        style={{
                            width: `${barMin}%`,
                            backgroundColor: barLeftColor || 'rgba(0, 0, 0, 0.25)',
                            height: '3px'
                        }}
                        onClick={onBarLeftClick}
                    />

                    {/* Active Range */}
                    <div
                        className="absolute rounded-full cursor-pointer"
                        style={{
                            left: `${barMin}%`,
                            width: `${100 - barMin - barMax}%`,
                            backgroundColor: barInnerColor || '#000',
                            height: '3px'
                        }}
                    >
                        {/* Inner bar clickable areas */}
                        <div
                            className="absolute left-0 w-1/2 h-full cursor-pointer"
                            onClick={onInnerBarLeftClick}
                        />
                        <div
                            className="absolute right-0 w-1/2 h-full cursor-pointer"
                            onClick={onInnerBarRightClick}
                        />
                    </div>

                    {/* Right clickable area */}
                    <div
                        className="absolute right-0 rounded-r-full cursor-pointer"
                        style={{
                            width: `${barMax}%`,
                            backgroundColor: barRightColor || 'rgba(0, 0, 0, 0.25)',
                            height: '3px'
                        }}
                        onClick={onBarRightClick}
                    />
                </div>

                {/* Hidden range inputs for accessibility */}
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={minVal}
                    ref={minValRef}
                    onChange={handleMinChange}
                    className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer opacity-0"
                    style={{ zIndex: 10 }}
                    disabled={disabled}
                    aria-label={`Minimum value: ${displayMinCaption}`}
                />

                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={maxVal}
                    ref={maxValRef}
                    onChange={handleMaxChange}
                    className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer opacity-0"
                    style={{ zIndex: 11 }}
                    disabled={disabled}
                    aria-label={`Maximum value: ${displayMaxCaption}`}
                />

                {/* Visual thumbs */}
                <div
                    className="absolute rounded-full cursor-grab active:cursor-grabbing shadow-md hover:shadow-lg transition-shadow"
                    style={{
                        left: `calc(${barMin}% - 14px)`,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: thumbLeftColor || '#7a8d1a',
                        width: '28px',
                        height: '28px',
                        zIndex: 20
                    }}
                    onMouseDown={onLeftThumbMousedown}
                    onTouchStart={onLeftThumbTouchStart}
                    role="slider"
                    aria-valuemin={min}
                    aria-valuemax={max}
                    aria-valuenow={minVal}
                    aria-label={`Minimum value: ${displayMinCaption}`}
                    tabIndex={disabled ? -1 : 0}
                >
                    {/* Thumb caption */}
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 hover:opacity-100 transition-opacity whitespace-nowrap">
                        {displayMinCaption}
                    </div>
                </div>

                <div
                    className="absolute rounded-full cursor-grab active:cursor-grabbing shadow-md hover:shadow-lg transition-shadow"
                    style={{
                        left: `calc(${100 - barMax}% - 14px)`,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: thumbRightColor || '#7a8d1a',
                        width: '28px',
                        height: '28px',
                        zIndex: 21
                    }}
                    onMouseDown={onRightThumbMousedown}
                    onTouchStart={onRightThumbTouchStart}
                    role="slider"
                    aria-valuemin={min}
                    aria-valuemax={max}
                    aria-valuenow={maxVal}
                    aria-label={`Maximum value: ${displayMaxCaption}`}
                    tabIndex={disabled ? -1 : 0}
                >
                    {/* Thumb caption */}
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 hover:opacity-100 transition-opacity whitespace-nowrap">
                        {displayMaxCaption}
                    </div>
                </div>
            </div>

            {/* Value Labels */}
            <div className="flex justify-between">
                <span className="text-sm font-medium" style={{ color: '#3b372e' }}>
                    {displayMinCaption}
                </span>
                <span className="text-sm font-medium" style={{ color: '#3b372e' }}>
                    {displayMaxCaption}
                </span>
            </div>
        </div>
    );
});

CustomSlider.displayName = 'CustomSlider';

export default CustomSlider;